/**
 * Form Schema Utilities - Validation and helper functions for form management
 */

/**
 * Validate form schema
 * @param {Object} form - Form object with role and fields
 * @returns {Object} Validation result { isValid: boolean, error?: string }
 */
export const validateFormSchema = (form) => {
  // Check required fields
  if (!form.role || typeof form.role !== "string") {
    return { isValid: false, error: "Form role is required and must be a string" };
  }

  if (!Array.isArray(form.fields) || form.fields.length === 0) {
    return { isValid: false, error: "Form must have at least one field" };
  }

  // Validate each field
  for (let i = 0; i < form.fields.length; i++) {
    const field = form.fields[i];
    const fieldValidation = validateField(field, i);

    if (!fieldValidation.isValid) {
      return fieldValidation;
    }
  }

  return { isValid: true };
};

/**
 * Validate individual field
 * @param {Object} field - Field object
 * @param {number} index - Field index
 * @returns {Object} Validation result
 */
export const validateField = (field, index) => {
  // Check required field properties
  if (!field.key || typeof field.key !== "string") {
    return {
      isValid: false,
      error: `Field ${index + 1}: Key is required and must be a string`,
    };
  }

  if (!field.label || typeof field.label !== "string") {
    return {
      isValid: false,
      error: `Field ${index + 1}: Label is required and must be a string`,
    };
  }

  if (!field.type || typeof field.type !== "string") {
    return {
      isValid: false,
      error: `Field ${index + 1}: Type is required and must be a string`,
    };
  }

  // Validate field type
  const validTypes = [
    "text",
    "email",
    "number",
    "textarea",
    "dropdown",
    "multiselect",
    "radio",
    "gender",
    "boolean",
    "date",
    "file",
    "multiSelectCheckbox",
  ];

  if (!validTypes.includes(field.type)) {
    return {
      isValid: false,
      error: `Field ${index + 1}: Invalid type "${field.type}". Must be one of: ${validTypes.join(", ")}`,
    };
  }

  // Validate options for dropdown/radio/multiselect/radio fields
  if ((field.type === "dropdown" || field.type === "radio" || field.type === "multiselect" || field.type === "multiSelectCheckbox" || field.type === "gender")) {
    if (!Array.isArray(field.options) || field.options.length === 0) {
      return {
        isValid: false,
        error: `Field ${index + 1} ("${field.label}"): ${field.type} fields must have at least one option`,
      };
    }

    // Check that all options are valid (either strings or objects with label/value)
    for (let i = 0; i < field.options.length; i++) {
      const option = field.options[i];

      if (typeof option === "string") {
        // Legacy format: plain string
        if (option.trim() === "") {
          return {
            isValid: false,
            error: `Field ${index + 1} ("${field.label}"): All options must be non-empty strings`,
          };
        }
      } else if (typeof option === "object" && option !== null) {
        // New format: object with label and value
        if (!option.label || typeof option.label !== "string" || option.label.trim() === "") {
          return {
            isValid: false,
            error: `Field ${index + 1} ("${field.label}"): All options must have a non-empty label`,
          };
        }
        if (!option.value || typeof option.value !== "string" || option.value.trim() === "") {
          return {
            isValid: false,
            error: `Field ${index + 1} ("${field.label}"): All options must have a non-empty value`,
          };
        }
      } else {
        return {
          isValid: false,
          error: `Field ${index + 1} ("${field.label}"): Invalid option format`,
        };
      }
    }
  }

  // Validate required is boolean
  if (typeof field.required !== "boolean") {
    return {
      isValid: false,
      error: `Field ${index + 1}: Required must be a boolean`,
    };
  }

  return { isValid: true };
};

/**
 * Generate form JSON structure
 * @param {string} role - Form role (e.g., 'rider', 'driver')
 * @param {Array} fields - Array of field objects
 * @param {number} version - Form version (default: 1)
 * @returns {Object} Generated form JSON
 */
export const generateFormJSON = (role, fields, version = 1, steps = null) => {
  // Define supported languages
  const LANGUAGES = ["en", "hi", "ta", "te", "kn", "bn", "gu", "mr", "hn"];

  // If steps are provided, use step-based structure
  if (steps && Array.isArray(steps) && steps.length > 0) {
    const cleanedSteps = steps.map((step) => {
      const cleanedStep = {
        step_id: step.step_id,
        title: step.title,
        description: step.description || "",
        fields: []
      };

      // Include multilingual titles and descriptions
      LANGUAGES.forEach((lang) => {
        if (lang !== "en") {
          const titleKey = `title_${lang}`;
          const descKey = `description_${lang}`;
          if (step[titleKey]) {
            cleanedStep[titleKey] = step[titleKey];
          }
          if (step[descKey]) {
            cleanedStep[descKey] = step[descKey];
          }
        }
      });

      // Clean fields in this step
      if (step.fields && Array.isArray(step.fields)) {
        cleanedStep.fields = step.fields.map((field) => {
          const cleanedField = {
            key: field.key.trim(),
            label: field.label.trim(),
            type: field.type,
            required: field.required,
            isFilter: field.isFilter || false,
            isVisibleToEmployer: field.isVisibleToEmployer || false,
            visibleToEmployerLabel: field.visibleToEmployerLabel || "",
            isRequiredInRegionFilter: field.isRequiredInRegionFilter || false,
          };

          // Add isRequiredInRegionFilter only for location fields
          // if (field.type === "location") {
          //   cleanedField.isRequiredInRegionFilter = field.isRequiredInRegionFilter || false;
          // }

          // Include multilingual labels
          LANGUAGES.forEach((lang) => {
            if (lang !== "en") {
              const labelKey = `label_${lang}`;
              if (field[labelKey]) {
                cleanedField[labelKey] = field[labelKey].trim();
              }
            }
          });

          // Include options only for dropdown/radio/multiselect/gender types
          if ((field.type === "dropdown" || field.type === "radio" || field.type === "multiselect" || field.type === "multiSelectCheckbox" || field.type === "gender") && field.options) {
            cleanedField.options = field.options
              .filter((opt) => {
                if (typeof opt === "string") return opt.trim() !== "";
                if (typeof opt === "object") return opt.label?.trim() && opt.value?.trim();
                return false;
              })
              .map((opt) => {
                if (typeof opt === "string") return opt.trim();
                return {
                  label: opt.label.trim(),
                  value: opt.value.trim()
                };
              });

            // Include multilingual options
            LANGUAGES.forEach((lang) => {
              if (lang !== "en") {
                const optionsKey = `options_${lang}`;
                if (field[optionsKey] && Array.isArray(field[optionsKey])) {
                  cleanedField[optionsKey] = field[optionsKey]
                    .filter((opt) => {
                      if (typeof opt === "string") return opt.trim() !== "";
                      if (typeof opt === "object") return opt.label?.trim() && opt.value?.trim();
                      return false;
                    })
                    .map((opt) => {
                      if (typeof opt === "string") return opt.trim();
                      return {
                        label: opt.label.trim(),
                        value: opt.value.trim()
                      };
                    });
                }
              }
            });
          }

          return cleanedField;
        });
      }

      return cleanedStep;
    });

    return {
      role: role.toLowerCase(),
      version: parseInt(version) || 1,
      steps: cleanedSteps,
    };
  }

  // Original flat fields structure (backward compatibility)
  const cleanedFields = fields.map((field) => {
    const cleanedField = {
      key: field.key.trim(),
      label: field.label.trim(),
      type: field.type,
      required: field.required,
      isFilter: field.isFilter || false,
      isVisibleToEmployer: field.isVisibleToEmployer || false,
      isRequiredInRegionFilter: field.isRequiredInRegionFilter || false,
    };

    // Add isRequiredInRegionFilter only for location fields
    // if (field.type === "location") {
    //   cleanedField.isRequiredInRegionFilter = field.isRequiredInRegionFilter || false;
    // }

    // Include multilingual labels
    LANGUAGES.forEach((lang) => {
      if (lang !== "en") {
        const labelKey = `label_${lang}`;
        if (field[labelKey]) {
          cleanedField[labelKey] = field[labelKey].trim();
        }
      }
    });

    // Include options only for dropdown/radio/multiselect/gender types
    if ((field.type === "dropdown" || field.type === "radio" || field.type === "multiselect" || field.type === "multiSelectCheckbox" || field.type === "gender") && field.options) {
      cleanedField.options = field.options
        .filter((opt) => {
          if (typeof opt === "string") return opt.trim() !== "";
          if (typeof opt === "object") return opt.label?.trim() && opt.value?.trim();
          return false;
        })
        .map((opt) => {
          if (typeof opt === "string") return opt.trim();
          return {
            label: opt.label.trim(),
            value: opt.value.trim()
          };
        });

      // Include multilingual options
      LANGUAGES.forEach((lang) => {
        if (lang !== "en") {
          const optionsKey = `options_${lang}`;
          if (field[optionsKey] && Array.isArray(field[optionsKey])) {
            cleanedField[optionsKey] = field[optionsKey]
              .filter((opt) => {
                if (typeof opt === "string") return opt.trim() !== "";
                if (typeof opt === "object") return opt.label?.trim() && opt.value?.trim();
                return false;
              })
              .map((opt) => {
                if (typeof opt === "string") return opt.trim();
                return {
                  label: opt.label.trim(),
                  value: opt.value.trim()
                };
              });
          }
        }
      });
    }

    return cleanedField;
  });

  return {
    role: role.toLowerCase(),
    version: parseInt(version) || 1,
    fields: cleanedFields,
    // createdAt: new Date().toISOString(),
  };
};

/**
 * Parse form JSON and convert to field objects for editing
 * @param {Object} form - Form JSON object
 * @returns {Array} Array of field objects
 */
export const parseFormFields = (form) => {
  if (!form || !Array.isArray(form.fields)) {
    return [];
  }

  return form.fields.map((field) => ({
    key: field.key,
    label: field.label,
    type: field.type,
    required: field.required || false,
    options: field.options || [],
  }));
};

/**
 * Check for duplicate field keys
 * @param {Array} fields - Array of field objects
 * @returns {Object} { hasDuplicates: boolean, duplicates?: string[] }
 */
export const checkDuplicateKeys = (fields) => {
  const keys = fields.map((f) => f.key.toLowerCase());
  const uniqueKeys = new Set(keys);

  if (keys.length !== uniqueKeys.size) {
    const duplicates = keys.filter(
      (key, index) => keys.indexOf(key) !== index
    );
    return {
      hasDuplicates: true,
      duplicates: [...new Set(duplicates)],
    };
  }

  return { hasDuplicates: false };
};

/**
 * Export form fields to CSV format
 * @param {string} role - Form role
 * @param {Array} fields - Array of field objects
 * @returns {string} CSV formatted data
 */
export const exportToCSV = (role, fields) => {
  const headers = ["Key", "Label", "Type", "Required", "Options"];
  const rows = fields.map((field) => [
    field.key,
    field.label,
    field.type,
    field.required ? "Yes" : "No",
    field.options ? field.options.join("; ") : "",
  ]);

  const csvContent = [
    `Form: ${role}`,
    headers.join(","),
    ...rows.map((row) =>
      row
        .map((cell) =>
          typeof cell === "string" && cell.includes(",")
            ? `"${cell}"`
            : cell
        )
        .join(",")
    ),
  ].join("\n");

  return csvContent;
};

/**
 * Validate email field
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Get field type display name
 * @param {string} type - Field type value
 * @returns {string} Display name
 */
export const getFieldTypeDisplayName = (type) => {
  const typeMap = {
    text: "Text",
    email: "Email",
    number: "Number",
    textarea: "Textarea",
    dropdown: "Dropdown",
    multiselect: "multiselect",
    radio: "Radio",
    gender: "Gender",
    boolean: "Boolean (Yes/No)",
    date: "Date",
    file: "File Upload",
    multiSelectCheckbox: "Multi-Select Checkbox",
  };
  return typeMap[type] || type;
};

/**
 * Get default field properties based on type
 * @param {string} type - Field type
 * @returns {Object} Default field properties
 */
export const getDefaultFieldProperties = (type) => {
  const defaults = {
    text: { key: "", label: "", type: "text", required: false },
    email: { key: "", label: "", type: "email", required: false },
    number: { key: "", label: "", type: "number", required: false },
    textarea: { key: "", label: "", type: "textarea", required: false },
    dropdown: {
      key: "",
      label: "",
      type: "dropdown",
      required: false,
      options: [],
    },
    multiselect: {
      key: "",
      label: "",
      type: "multiselect",
      required: false,
      options: [],
    },
    radio: {
      key: "",
      label: "",
      type: "radio",
      required: false,
      options: [],
    },
    gender: {
      key: "",
      label: "",
      type: "gender",
      required: false,
      options: [],
    },
    boolean: {
      key: "",
      label: "",
      type: "boolean",
      required: false,
    },
    date: { key: "", label: "", type: "date", required: false },
    file: { key: "", label: "", type: "file", required: false },
    multiSelectCheckbox: {
      key: "",
      label: "",
      type: "multiSelectCheckbox",
      required: false,
      options: [],
    },
  };

  return defaults[type] || defaults.text;
};
